// OnboardingUI.tsx
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Fade,
  Alert,
  List,
  ListItem,
  ListItemText,
  AppBar,
} from "@mui/material";
import React, { JSX } from "react";
import { useTranslation } from "react-i18next";

import LanguageSelector from "0.Mine/0.Components/Profile/LanguageSwitcher";
import { SafeArgonTypography } from "0.Mine/4.Argon/ArgonComponents";

export interface OnboardingUIProps {
  activeStep: number;
  steps: { label: string; description: string; component: JSX.Element }[];
  contentVisible: boolean;
  fadeDuration: number;
  handleNext: () => void;
  handleBack: () => void;
  isStepValid: boolean;
  validationErrors: string[];
}

const OnboardingUI: React.FC<OnboardingUIProps> = ({
  activeStep,
  steps,
  contentVisible,
  fadeDuration,
  handleNext,
  handleBack,
  isStepValid,
  validationErrors,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      className="onboarding_container"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        overflow: "hidden",
        position: "relative",
        bgcolor: "#E4E4E4", // Changed background color
      }}
    >
      {/* Sidebar with Stepper */}
      <Box
        className="onboarding_sidebar"
        sx={{
          width: 360, // Increased width
          background: "linear-gradient(135deg, #362A84 0%, #2E2851 100%)",
          color: "#fff",
          p: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
          borderRadius: 15, // Border radius increased to 30px
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          m: 2,
          height: "calc(100% - 32px)",
        }}
      >
        <Box sx={{ mb: 6, mt: 2.5 }}>
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "26px", // Logo size 26px
              marginBottom: "20px", // Margin 20px
            }}
          >
            <span style={{ color: "#fff" }}>q</span>
            <span style={{ color: "#fff", position: "relative" }}>
              l
              <span
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-4px",
                  fontSize: "70%",
                  color: "#fff",
                }}
              >
                ●
              </span>
            </span>
            <span style={{ color: "#fff" }}>ckup</span>
          </Typography>
        </Box>
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label} completed={index < activeStep}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: index === activeStep ? "#fff" : "rgba(255,255,255,0.5)",
                    "& .MuiStepIcon-text": { fill: "#2E2851" },
                    fontSize: "22px", // Larger step icon
                  },
                }}
                sx={{
                  "& .MuiStepLabel-label": {
                    color: "#fff",
                    opacity: index <= activeStep ? 1 : 0.7,
                    fontSize: "18px", // Step text size 18px
                    fontWeight: 500, // Weight 500
                    letterSpacing: "1px", // 1px spacing
                  },
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Add Language Selector to bottom of sidebar */}
        <Box
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {/*<LanguageSelector />*/}
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 14,
            pt: 4,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <SafeArgonTypography
              variant="h4"
              sx={{
                mb: 2,
                color: "#36376D", // Changed text color
              }}
            >
              {steps[activeStep]?.label || "Complete Your Business Profile"}
            </SafeArgonTypography>
            <SafeArgonTypography
              variant="p"
              sx={{
                color: "#36376D", // Changed text color
                mb: 2,
              }}
            >
              {steps[activeStep]?.description ||
                "Please fill in the required information to proceed."}
            </SafeArgonTypography>
          </Box>
        </Box>

        <Box
          sx={{
            px: 14,
            pt: 4,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            overflow: "auto",

            // Apply form field styles globally
            "& .MuiOutlinedInput-root": {
              bgcolor: "#DDDDDD", // Background color for fields
              "& fieldset": {
                borderColor: "#CECECE", // Border color
              },
            },
            "& .MuiInputBase-input": {
              color: "#36376D", // Text color for inputs
            },
            "& .MuiInputLabel-root": {
              color: "#36376D", // Label color
            },
          }}
        >
          {validationErrors.length > 0 && (
            <Box sx={{ width: "100%", maxWidth: 1000, mb: 2 }}>
              <Alert severity="info">
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1, color: "#36376D" }}
                >
                  {t("Per proseguire")}
                </Typography>
                <List dense disablePadding>
                  {validationErrors.map((error, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemText
                        primary={`• ${error}`}
                        primaryTypographyProps={{ color: "#36376D" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            </Box>
          )}

          <Fade in={contentVisible} timeout={fadeDuration}>
            <Box sx={{ width: "100%", maxWidth: 1000 }}>{steps[activeStep].component}</Box>
          </Fade>
        </Box>

        <Box
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#E4E4E4", // Changed to match page background
          }}
        >
          <Fade in={contentVisible} timeout={fadeDuration * 2.5}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                maxWidth: 600,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon sx={{ fontSize: 40 }} />} // Increased icon size to 40px
                sx={{
                  textTransform: "none",
                  color: "#36376D",
                  borderColor: "#36376D",
                  "&:hover": {
                    borderColor: "#36376D",
                    backgroundColor: "rgba(54, 55, 109, 0.04)",
                  },
                }}
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Indietro
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 40 }} />} // Added forward arrow with size 40px
                sx={{
                  bgcolor: isStepValid ? "#36376D" : "#ccc",
                  textTransform: "none",
                  "&:hover": { bgcolor: isStepValid ? "#2E2851" : "#ccc" },
                }}
                onClick={handleNext}
                disabled={!isStepValid}
              >
                {activeStep === steps.length - 1 ? t("Finito!") : t("Avanti")}
              </Button>
            </Box>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingUI;
